//EnviosApp

import React, { useState, useEffect, useMemo } from 'react';
import { 
    Send, User, Loader2, Copy, CheckCircle, ArrowLeft, MapPin, 
    Ticket, Wallet, Check, ShieldCheck, 
    AlertTriangle, Sparkles, Gift, X, Timer, ShoppingBag, Bike
} from 'lucide-react';
import { QRCodeSVG } from 'qrcode.react'; 
import api from '../services/api';
import toast from 'react-hot-toast';
import axios from 'axios';

export default function EnviosApp({ cart, total, estabelecimento, onBack, onFinalize, taxaEntregaSoma, socket, cepInicial }) {
    const [loading, setLoading] = useState(false);
    const [loadingCep, setLoadingCep] = useState(false);
    const [paymentStep, setPaymentStep] = useState('form'); 
    const [paymentData, setPaymentData] = useState(null);
    const [orderStatus, setOrderStatus] = useState('pendente'); 
    const [showAccessModal, setShowAccessModal] = useState(false);
    const [timeLeft, setTimeLeft] = useState(600); 
     
    // Estados para controle do timer e liberação do Pix Direto (Estático)
    const [timerPixDireto, setTimerPixDireto] = useState(40);
    const [pixDiretoPronto, setPixDiretoPronto] = useState(false);
     
    const [taxaAtual, setTaxaAtual] = useState(Number(taxaEntregaSoma) || 0);
    const [taxaConsultada, setTaxaConsultada] = useState(Number(taxaEntregaSoma) > 0);
    const [cupomInput, setCupomInput] = useState('');
    const [cupomData, setCupomData] = useState(null);
    const [loadingCupom, setLoadingCupom] = useState(false);
     
    const [precisaTroco, setPrecisaTroco] = useState(null); 
    const [trocoPara, setTrocoPara] = useState('');
     
    const [formData, setFormData] = useState({
        nome: '', cpf: '', cep: cepInicial || '', whatsapp: '', endereco: '',
        numero: '', complemento: '', bairro: '', cidade: '',
        pagamento: 'Pix', email: '' 
    });

    // Se houver um CEP inicial vindo da sacola e os campos de endereço ainda estiverem vazios, fazemos a busca automática
    useEffect(() => {
        if (cepInicial && cepInicial.length === 8 && !formData.endereco) {
            consultarCepAutomatico(cepInicial);
        }
    }, [cepInicial]);

    const consultarCepAutomatico = async (cepStr) => {
        const cepLimpo = cepStr.replace(/\D/g, '');
        if (cepLimpo.length !== 8) return;
        
        setLoadingCep(true);
        try {
            const { data } = await axios.get(`https://viacep.com.br/ws/${cepLimpo}/json/`);
            
            if (!data.erro) {
                const novoEndereco = { 
                    ...formData, 
                    cep: cepLimpo, 
                    endereco: data.logradouro, 
                    bairro: data.bairro, 
                    cidade: data.localidade,
                    estado: data.uf 
                };
                setFormData(novoEndereco);

                const resFrete = await api.post(`/cardapio/estabelecimentos/${estabelecimento.id}/calcular-frete`, {
                    endereco_cliente: novoEndereco
                });

                if (resFrete.data.success) {
                    setTaxaAtual(Number(resFrete.data.taxa_entrega));
                    setTaxaConsultada(true);
                } else if (resFrete.data.fora_da_area) {
                    setTaxaAtual(0);
                    setTaxaConsultada(false);
                    toast.error(resFrete.data.mensagem || "Fora da área de entrega.");
                }
            }
        } catch (error) {
            console.error("Erro frete inicial:", error);
        } finally {
            setLoadingCep(false);
        }
    };

    // --- CÁLCULO DE TOTAIS ---
    const { valorDesconto, valorTotalReal, isFidelidade, nomePremio } = useMemo(() => {
        let desconto = 0;
        let fidelidadeAtiva = false;
        let premio = '';

        if (cupomData) {
            if (cupomData.tipo === 'fidelidade') {
                fidelidadeAtiva = true;
                premio = cupomData.premio || "Brinde Especial";
                desconto = Number(cupomData.valor || 0); 
            } else {
                desconto = cupomData.tipo === 'porcentagem' 
                    ? (Number(total) * Number(cupomData.valor)) / 100 
                    : Number(cupomData.valor);
            }
        }

        const totalFinal = Number((Math.max(0, (Number(total) - desconto) + Number(taxaAtual))).toFixed(2));
         
        return { 
            valorDesconto: Number(desconto.toFixed(2)), 
            valorTotalReal: totalFinal,
            isFidelidade: fidelidadeAtiva,
            nomePremio: premio
        };
    }, [total, cupomData, taxaAtual]);
     
    // PLANO B: Se o socket falhar ou eu mudar no BD manualmente, este cara pega a mudança
    useEffect(() => {
        let interval;
        const idPedido = paymentData?.pedido_id || paymentData?.pedidoId;

        if (paymentStep === 'pix_display' && orderStatus === 'pendente' && idPedido) {
            interval = setInterval(async () => {
                try {
                    const res = await api.get(`/pagamento/status/${idPedido}`);
                    
                    if (res.data.status === 'pago') {
                        console.log("✅ Pagamento detectado via Polling (Backup)!");
                        setOrderStatus('pago');
                        
                        try {
                            await enviarWhatsAppAutomático(idPedido);
                        } catch (e) { console.error("Erro notificação backup:", e); }

                        clearInterval(interval);
                        
                        setTimeout(() => {
                            setShowAccessModal(true);
                        }, 2500);
                    }
                } catch (err) {
                    console.error("Erro ao consultar status no backup:", err);
                }
            }, 5000);
        }

        return () => clearInterval(interval);
    }, [paymentStep, orderStatus, paymentData]);

    // Timer do PIX (Geral)
    useEffect(() => {
        if (paymentStep === 'pix_display' && timeLeft > 0) {
            const timer = setInterval(() => setTimeLeft(prev => prev - 1), 1000);
            return () => clearInterval(timer);
        }
    }, [paymentStep, timeLeft]);

    // Timer de 40 segundos específico para o Pix Direto
    useEffect(() => {
        let interval;
        if (paymentStep === 'pix_display' && timerPixDireto > 0 && !pixDiretoPronto) {
            interval = setInterval(() => {
                setTimerPixDireto(prev => {
                    if (prev <= 1) {
                        setPixDiretoPronto(true);
                        return 0;
                    }
                    return prev - 1;
                });
            }, 1000);
        }
        return () => clearInterval(interval);
    }, [paymentStep, timerPixDireto, pixDiretoPronto]);

    const formatTime = (seconds) => {
        const mins = Math.floor(seconds / 60);
        const secs = seconds % 60;
        return `${mins}:${secs.toString().padStart(2, '0')}`;
    };

    // --- VALIDAÇÃO DINÂMICA DE TROCO ---
    const trocoInvalido = useMemo(() => {
        if (formData.pagamento !== 'Dinheiro' || precisaTroco !== 'sim' || !trocoPara) return false;
        const valorDigitado = parseFloat(trocoPara);
        return isNaN(valorDigitado) || valorDigitado <= valorTotalReal;
    }, [formData.pagamento, precisaTroco, trocoPara, valorTotalReal]);

    // Busca CEP e Taxa
    const checkCEP = async (e) => {
        const cep = e.target.value.replace(/\D/g, '');
        if (cep.length !== 8) return;
        
        setLoadingCep(true);
        try {
            const { data } = await axios.get(`https://viacep.com.br/ws/${cep}/json/`);
            
            if (!data.erro) {
                const novoEndereco = { 
                    ...formData, 
                    cep, 
                    endereco: data.logradouro, 
                    bairro: data.bairro, 
                    cidade: data.localidade,
                    estado: data.uf 
                };
                setFormData(novoEndereco);

                const resFrete = await api.post(`/cardapio/estabelecimentos/${estabelecimento.id}/calcular-frete`, {
                    endereco_cliente: novoEndereco
                });

                if (resFrete.data.success) {
                    setTaxaAtual(Number(resFrete.data.taxa_entrega));
                    setTaxaConsultada(true);
                    toast.success(`Frete calculado: R$ ${resFrete.data.taxa_entrega.replace('.', ',')}`);
                } else if (resFrete.data.fora_da_area) {
                    setTaxaAtual(0);
                    setTaxaConsultada(false);
                    toast.error(resFrete.data.mensagem || "Fora da área de entrega.");
                }
            } else {
                toast.error("CEP não encontrado.");
            }
        } catch (error) {
            console.error("Erro frete:", error);
            toast.error("Erro ao calcular frete. Verifique sua conexão.");
        } finally {
            setLoadingCep(false);
        }
    };

    // Recalcula o frete se o número mudar e o CEP já tiver sido consultado
    useEffect(() => {
        if (formData.cep.length === 8 && formData.numero.length > 0 && taxaConsultada) {
            const timer = setTimeout(() => {
                checkCEP({ target: { value: formData.cep } });
            }, 1000);
            return () => clearTimeout(timer);
        }
    }, [formData.numero]);

    const aplicarCupom = async (e) => {
        if (e) e.preventDefault(); // Impede comportamento padrão de submit do formulário
        if (!cupomInput.trim()) return;

        const emailCliente = formData?.email || localStorage.getItem('cliente_email') || '';
        const whatsappCliente = formData?.whatsapp || formData?.telefone || localStorage.getItem('cliente_whatsapp') || '';
        const cpfCliente = formData?.cpf || localStorage.getItem('cliente_cpf') || '';
        const clienteId = localStorage.getItem('cliente_id') || null;

        setLoadingCupom(true);
        try {
            const res = await api.post('/marketing/cupons/validar', {
                codigo: cupomInput,
                estabelecimento_id: estabelecimento.id,
                valor_carrinho: total,
                cliente_id: clienteId,
                email: emailCliente,
                whatsapp: whatsappCliente,
                cpf: cpfCliente
            });

            setCupomData(res.data);
            setCupomInput('');
            toast.success("Benefício aplicado!");
        } catch (err) {
            setCupomData(null);
            toast.error(err.response?.data?.error || "Código inválido.");
        } finally { 
            setLoadingCupom(false); 
        }
    };

    const processarPedido = async (e) => {
        if (e) e.preventDefault(); // Impede comportamento padrão de submit do formulário
        
        if (!formData.nome || !formData.whatsapp || !formData.endereco || !formData.numero || !formData.bairro || !formData.cpf) {
            return toast.error("Preencha todos os campos obrigatórios.");
        }
        
        if (!taxaConsultada) return toast.error("Calcule a taxa de entrega informando o CEP.");
        
        if (formData.pagamento === 'Dinheiro' && precisaTroco === 'sim' && trocoInvalido) return;

        setLoading(true);
        try {
            const payload = {
                cliente: { 
                    ...formData, 
                    cpf: formData.cpf.replace(/\D/g, ''), 
                    whatsapp: formData.whatsapp.replace(/\D/g, '') 
                },
                total: valorTotalReal,
                desconto_valor: valorDesconto,
                cupom_codigo: cupomData?.codigo || null,
                metodo: formData.pagamento,
                estabelecimento_id: estabelecimento.id,
                cart: cart.map(item => ({ ...item, taxa_entrega: Number(taxaAtual) })),
                troco: (formData.pagamento === 'Dinheiro' && precisaTroco === 'sim') ? Number(trocoPara) : null,
                observacao_fidelidade: isFidelidade ? `BRINDE: ${nomePremio}` : null
            };

            const response = await api.post('/pagamento/processar', payload);

            setPaymentData(response.data);

            if (formData.pagamento === 'Pix') {
                setPaymentStep('pix_display');
            } else { 
                setShowAccessModal(true); 
            }

        } catch (error) { 
             console.error("Erro detalhado do servidor:", error.response?.data);
            toast.error(error.response?.data?.error || "Erro ao gerar pedido."); 
        } finally { 
            setLoading(false); 
        }
    };

    const enviarWhatsApp = async (metodo, endereco) => {
        const loadingToast = toast.loading("Enviando seu acesso...");
        try {
            const whatsRaw = formData.whatsapp || "";
            let whatsappLimpo = whatsRaw.replace(/\D/g, '');
            
            if (!whatsappLimpo) {
                toast.error("Número de WhatsApp não informado.", { id: loadingToast });
                return;
            }

            if (whatsappLimpo.length <= 11) {
                whatsappLimpo = `55${whatsappLimpo}`;
            }

            await api.post(`/whatsapp/add-contact/${estabelecimento.id}`, {
                nome: formData.nome,
                email: formData.email,
                whatsapp: whatsappLimpo, 
                cpf: formData.cpf.replace(/\D/g, ''),
                endereco: endereco
            });

            const resTemplates = await api.get(`/whatsapp/templates/${estabelecimento.id}`);
            const template = resTemplates.data.payload.find(t => t.NomeTemplate === 'PedidoEnviado');

            if (template) {
                const idDoPedido = paymentData?.pedido_id || paymentData?.pedidoId;

                await api.post(`/whatsapp/send/${estabelecimento.id}`, {
                    number: whatsappLimpo, 
                    templateId: template.id,
                    pedido_id: idDoPedido 
                });
                
                toast.success("Enviado com sucesso!", { id: loadingToast });
            } else {
                toast.error("Template 'PedidoEnviado' não encontrado no sistema.", { id: loadingToast });
            }
            
            onFinalize();
        } catch (e) {
            console.error("Erro detalhes:", e.response?.data);
            toast.error(e.response?.data?.error || "Erro ao enviar WhatsApp.", { id: loadingToast });
        }
    };

    const cardStyle = { background: '#fff', borderRadius: '24px', padding: '20px', marginBottom: '15px', boxShadow: '0 4px 6px -1px rgba(0,0,0,0.05)' };
    const labelStyle = { display: 'block', fontSize: '11px', fontWeight: '900', color: '#64748b', marginBottom: '8px', letterSpacing: '0.5px' };
    const inputStyle = { width: '100%', padding: '14px', borderRadius: '12px', border: '1.5px solid #e2e8f0', fontSize: '15px', outline: 'none' };

    return (
        <div style={{ display: 'flex', flexDirection: 'column', height: '100%', background: '#f8fafc', fontFamily: 'Inter, sans-serif' }}>
            {paymentStep === 'form' ? (
                <>
                    <div style={{ background: '#fff', padding: '20px', display: 'flex', alignItems: 'center', gap: '15px', borderBottom: '1px solid #e2e8f0' }}>
                        <button type="button" onClick={onBack} style={{ background: '#f1f5f9', border: 'none', padding: '10px', borderRadius: '12px', cursor: 'pointer' }}><ArrowLeft size={20}/></button>
                        <h3 style={{ margin: 0, fontWeight: '900', fontSize: '20px', color: '#0f172a' }}>Checkout</h3>
                    </div>

                    <div style={{ flex: 1, overflowY: 'auto', padding: '20px' }}>
                        
                        {cupomData && (
                            <div style={{ 
                                background: '#dcfce7', border: '2px solid #16a34a', borderRadius: '20px', padding: '15px', 
                                marginBottom: '20px', display: 'flex', alignItems: 'center', gap: '12px'
                            }}>
                                <div style={{ background: '#16a34a', padding: '10px', borderRadius: '14px', color: '#fff' }}>
                                    {isFidelidade ? <Gift size={22} /> : <Ticket size={22} />}
                                </div>
                                <div style={{ flex: 1 }}>
                                    <h4 style={{ margin: 0, fontSize: '12px', fontWeight: '900', color: '#166534' }}>
                                        {isFidelidade ? 'RECOMPENSA LIBERADA!' : 'CUPOM ATIVADO!'}
                                    </h4>
                                    <p style={{ margin: 0, fontSize: '14px', color: '#166534', fontWeight: '700' }}>
                                        {isFidelidade ? nomePremio : `Economia de R$ ${valorDesconto.toFixed(2)}`}
                                    </p>
                                </div>
                                <button type="button" onClick={() => setCupomData(null)} style={{ background: 'none', border: 'none', color: '#16a34a', cursor: 'pointer' }}><X size={20}/></button>
                            </div>
                        )}

                        {/* 1. IDENTIFICAÇÃO */}
                        <div style={cardStyle}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '15px', color: '#ef4444' }}>
                                <User size={18} /><span style={{ fontSize: '12px', fontWeight: '900' }}>IDENTIFICAÇÃO</span>
                            </div>
                            <input value={formData.nome} onChange={(e) => setFormData({ ...formData, nome: e.target.value })} style={{...inputStyle, marginBottom: '12px'}} placeholder="Nome Completo *" />
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                                <input value={formData.whatsapp} onChange={(e) => setFormData({ ...formData, whatsapp: e.target.value })} style={inputStyle} placeholder="WhatsApp *" />
                                <input value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} style={inputStyle} placeholder="Email *" />
                                <input value={formData.cpf} onChange={(e) => setFormData({ ...formData, cpf: e.target.value })} style={inputStyle} placeholder="CPF *" />
                            </div>
                        </div>

                        {/* 2. ENTREGA */}
                        <div style={cardStyle}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '15px', color: '#ef4444' }}>
                                <MapPin size={18} /><span style={{ fontSize: '12px', fontWeight: '900' }}>ENTREGA</span>
                            </div>
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1.2fr', gap: '12px', marginBottom: '12px' }}>
                                <div style={{ position: 'relative' }}>
                                    <input value={formData.cep} onChange={(e) => setFormData({...formData, cep: e.target.value})} onBlur={checkCEP} style={inputStyle} placeholder="CEP" />
                                    {loadingCep && <Loader2 size={16} className="animate-spin" style={{ position: 'absolute', right: '12px', top: '15px' }}/>}
                                </div>
                                <input value={formData.bairro} onChange={(e) => setFormData({...formData, bairro: e.target.value})} style={inputStyle} placeholder="Bairro" />
                            </div>
                            <input value={formData.endereco} onChange={(e) => setFormData({...formData, endereco: e.target.value})} style={{...inputStyle, marginBottom: '12px'}} placeholder="Rua / Avenida" />
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1.5fr', gap: '12px' }}>
                                <input value={formData.numero} onChange={(e) => setFormData({...formData, numero: e.target.value})} style={inputStyle} placeholder="Nº" />
                                <input value={formData.complemento} onChange={(e) => setFormData({...formData, complemento: e.target.value})} style={inputStyle} placeholder="Complemento" />
                            </div>
                        </div>

                        {/* 3. PAGAMENTO E CUPOM */}
                        <div style={cardStyle}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '15px', color: '#ef4444' }}>
                                <Wallet size={18} /><span style={{ fontSize: '12px', fontWeight: '900' }}>PAGAMENTO</span>
                            </div>
                            <select value={formData.pagamento} onChange={(e) => setFormData({...formData, pagamento: e.target.value})} style={{ ...inputStyle, fontWeight: '700', marginBottom: '15px' }}>
                                <option value="Pix">Pix</option>
                                <option value="Cartão">Cartão na Entrega</option>
                                <option value="Dinheiro">Dinheiro</option>
                            </select>

                            {formData.pagamento === 'Dinheiro' && (
                                <div style={{ background: '#f8fafc', padding: '15px', borderRadius: '16px', border: '1px solid #e2e8f0' }}>
                                    <label style={labelStyle}>PRECISA DE TROCO?</label>
                                    <div style={{ display: 'flex', gap: '10px', marginBottom: '12px' }}>
                                        <button type="button" onClick={() => setPrecisaTroco('sim')} style={{ flex: 1, padding: '12px', borderRadius: '12px', border: 'none', background: precisaTroco === 'sim' ? '#ef4444' : '#fff', color: precisaTroco === 'sim' ? '#fff' : '#64748b', fontWeight: 'bold', boxShadow: '0 2px 4px rgba(0,0,0,0.05)', cursor: 'pointer' }}>Sim</button>
                                        <button type="button" onClick={() => { setPrecisaTroco('nao'); setTrocoPara(''); }} style={{ flex: 1, padding: '12px', borderRadius: '12px', border: 'none', background: precisaTroco === 'nao' ? '#ef4444' : '#fff', color: precisaTroco === 'nao' ? '#fff' : '#64748b', fontWeight: 'bold', boxShadow: '0 2px 4px rgba(0,0,0,0.05)', cursor: 'pointer' }}>Não</button>
                                    </div>
                                    {precisaTroco === 'sim' && (
                                        <div style={{ position: 'relative' }}>
                                            <span style={{ position: 'absolute', left: '15px', top: '14px', fontWeight: '900', color: '#94a3b8' }}>R$</span>
                                            <input type="number" value={trocoPara} onChange={(e) => setTrocoPara(e.target.value)} placeholder={`Mínimo ${valorTotalReal.toFixed(2)}`} style={{ ...inputStyle, paddingLeft: '40px', borderColor: trocoInvalido ? '#ef4444' : '#e2e8f0' }} />
                                            {trocoInvalido && trocoPara && <small style={{ color: '#ef4444', fontWeight: 'bold', display: 'block', marginTop: '5px' }}>Valor deve ser maior que o total.</small>}
                                        </div>
                                    )}
                                </div>
                            )}

                            <div style={{ marginTop: '20px' }}>
                            <label style={labelStyle}>APLICAR CUPOM</label>
                            <div style={{ display: 'flex', gap: '8px' }}>
                                <input 
                                    value={cupomInput} 
                                    onChange={(e) => setCupomInput(e.target.value.toUpperCase())} 
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter') {
                                            e.preventDefault();
                                            aplicarCupom(e);
                                        }
                                    }}
                                    placeholder="CÓDIGO" 
                                    style={{ ...inputStyle, flex: 1, borderStyle: 'dashed', textAlign: 'center' }} 
                                />
                                <button 
                                    type="button" 
                                    onClick={(e) => {
                                        e.preventDefault();
                                        aplicarCupom(e);
                                    }} 
                                    style={{ background: '#0f172a', color: '#fff', border: 'none', padding: '0 20px', borderRadius: '12px', cursor: 'pointer' }}
                                >
                                    {loadingCupom ? '...' : 'OK'}
                                </button>
                            </div>
                            </div>
                            </div>
                        </div>
                 

                    {/* RODAPÉ RESUMO */}
                    <div style={{ background: '#fff', padding: '25px', borderRadius: '32px 32px 0 0', boxShadow: '0 -8px 20px rgba(0,0,0,0.05)' }}>
                        <div style={{ marginBottom: '15px' }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '14px', color: '#64748b', marginBottom: '4px' }}>
                                <span>Subtotal</span>
                                <span>R$ {total.toFixed(2).replace('.', ',')}</span>
                            </div>
                            
                            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '14px', color: '#64748b', marginBottom: '4px' }}>
                                <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                    <Bike size={14} /> Entrega ({formData.bairro || 'Informar'})
                                </span>
                                <span style={{ 
                                    color: taxaConsultada ? '#22c55e' : '#ef4444', 
                                    fontWeight: '700' 
                                }}>
                                    {loadingCep ? (
                                        <Loader2 size={14} className="animate-spin" />
                                    ) : taxaConsultada ? (
                                        `R$ ${taxaAtual.toFixed(2).replace('.', ',')}`
                                    ) : (
                                        'Aguardando CEP...'
                                    )}
                                </span>
                            </div>
                            
                            {valorDesconto > 0 && (
                                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '14px', color: '#22c55e', fontWeight: '700' }}>
                                    <span>Desconto</span>
                                    <span>- R$ {valorDesconto.toFixed(2).replace('.', ',')}</span>
                                </div>
                            )}
                            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '22px', fontWeight: '900', color: '#0f172a', borderTop: '2px dashed #f1f5f9', marginTop: '10px', paddingTop: '10px' }}>
                                <span>Total</span>
                                <span style={{ color: '#ef4444' }}>R$ {valorTotalReal.toFixed(2).replace('.', ',')}</span>
                            </div>
                        </div>
                        <button type="button" onClick={processarPedido} disabled={loading || trocoInvalido} style={{ width: '100%', background: trocoInvalido ? '#cbd5e1' : '#ef4444', color: '#fff', border: 'none', padding: '20px', borderRadius: '18px', fontWeight: '900', fontSize: '18px', cursor: trocoInvalido ? 'not-allowed' : 'pointer' }}>
                            {loading ? <Loader2 className="animate-spin" /> : 'FINALIZAR PEDIDO'}
                        </button>
                    </div>
                </>
            ) : (
                /* TELA PIX - LAYOUT NOVO TIPO SPLIT */
                <div style={{ flex: 1, background: '#fff', display: 'flex', flexDirection: 'column' }}>
                    <div style={{ display: 'flex', flex: 1, minHeight: 0 }}>
                        
                        {/* LADO ESQUERDO: RESUMO E BANNER */}
                        <div style={{ flex: 1, background: '#f8fafc', padding: '30px', borderRight: '1px solid #e2e8f0', overflowY: 'auto' }}>
                            
                            {/* HEADER */}
                            <div style={{ background: '#0f172a', color: '#fff', padding: '20px', borderRadius: '20px', marginBottom: '20px', textAlign: 'center' }}>
                                <Sparkles size={24} style={{ marginBottom: '10px' }} />
                                <h4 style={{ margin: 0, fontSize: '18px' }}>Pedido Confirmado!</h4>
                                <p style={{ fontSize: '12px', opacity: 0.8 }}>
                                    Finalize o pagamento para processarmos sua entrega.
                                </p>
                            </div>
                
                            {/* DESCONTO */}
                            {valorDesconto > 0 && (
                                <div style={{
                                    background: '#dcfce7',
                                    color: '#166534',
                                    padding: '15px',
                                    borderRadius: '15px',
                                    marginBottom: '20px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '10px'
                                }}>
                                    <Ticket size={20} />
                                    <div>
                                        <strong style={{ fontSize: '12px', display: 'block' }}>
                                            DESCONTO APLICADO
                                        </strong>
                                        <span style={{ fontSize: '14px' }}>
                                            Você economizou R$ {valorDesconto.toFixed(2).replace('.', ',')}
                                        </span>
                                    </div>
                                </div>
                            )}
                
                            {/* ITENS */}
                            <div style={{ textAlign: 'left' }}>
                                <h5 style={{
                                    fontSize: '12px',
                                    fontWeight: '900',
                                    color: '#64748b',
                                    marginBottom: '15px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '5px'
                                }}>
                                    <ShoppingBag size={14} /> RESUMO DO PEDIDO
                                </h5>
                
                                {cart.map((item, i) => (
                                    <div key={i} style={{
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        marginBottom: '8px',
                                        fontSize: '14px'
                                    }}>
                                        <span>{item.quantidade}x {item.nome}</span>
                                        <span style={{ fontWeight: 'bold' }}>
                                            R$ {Number(item.precoTotal).toFixed(2).replace('.', ',')}
                                        </span>
                                    </div>
                                ))}
                
                                {/* RESUMO FINANCEIRO */}
                                <hr style={{ border: 'none', borderTop: '1px dashed #cbd5e1', margin: '15px 0' }} />
                
                                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '14px', marginBottom: '5px' }}>
                                    <span>Subtotal</span>
                                    <span>R$ {Number(total).toFixed(2).replace('.', ',')}</span>
                                </div>
                
                                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '14px', marginBottom: '5px' }}>
                                    <span>Entrega</span>
                                    <span style={{ color: '#22c55e', fontWeight: '700' }}>
                                        {taxaAtual > 0 
                                            ? `R$ ${Number(taxaAtual).toFixed(2).replace('.', ',')}` 
                                            : 'Grátis'}
                                    </span>
                                </div>
                
                                {valorDesconto > 0 && (
                                    <div style={{
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        fontSize: '14px',
                                        color: '#22c55e',
                                        fontWeight: '700'
                                    }}>
                                        <span>Desconto</span>
                                        <span>- R$ {Number(valorDesconto).toFixed(2).replace('.', ',')}</span>
                                    </div>
                                )}
                
                                <hr style={{ border: 'none', borderTop: '1px dashed #cbd5e1', margin: '15px 0' }} />
                
                                <div style={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    fontWeight: '900',
                                    fontSize: '18px'
                                }}>
                                    <span>Total Final</span>
                                    <span style={{ color: '#ef4444' }}>
                                        R$ {Number(valorTotalReal).toFixed(2).replace('.', ',')}
                                    </span>
                                </div>
                            </div>
                        </div>
                
                        {/* LADO DIREITO: PIX */}
                        <div style={{
                            flex: 1.2,
                            padding: '30px',
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center'
                        }}>
                            <div style={{
                                background: '#fef2f2',
                                color: '#ef4444',
                                padding: '10px 20px',
                                borderRadius: '30px',
                                fontSize: '12px',
                                fontWeight: '900',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '8px',
                                marginBottom: '20px'
                            }}>
                                <Timer size={16}/> EXPIRA EM: {formatTime(timeLeft)}
                            </div>
                
                            <div style={{
                                border: '2px solid #f1f5f9',
                                padding: '20px',
                                borderRadius: '30px',
                                background: '#fff',
                                marginBottom: '20px'
                            }}>
                                <QRCodeSVG value={paymentData?.copy_paste || ''} size={200} />
                            </div>
                
                            <p style={{
                                fontSize: '12px',
                                color: '#64748b',
                                maxWidth: '280px',
                                marginBottom: '20px',
                                textAlign: 'center'
                            }}>
                                Abra o app do seu banco, escolha <b>Pagar com Pix</b> e escaneie o QR Code ou cole o código abaixo.
                            </p>
                
                            <button
                                type="button"
                                onClick={() => {
                                    navigator.clipboard.writeText(paymentData?.copy_paste);
                                    toast.success("Copiado!");
                                }}
                                style={{
                                    width: '100%',
                                    maxWidth: '280px',
                                    background: '#0f172a',
                                    color: '#fff',
                                    border: 'none',
                                    padding: '14px',
                                    borderRadius: '15px',
                                    fontWeight: '800',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    gap: '10px',
                                    marginBottom: '10px',
                                    cursor: 'pointer'
                                }}
                            >
                                <Copy size={18}/> COPIAR CÓDIGO PIX
                            </button>
                
                            {/* BOTÃO COM TIMER DE 40 SEGUNDOS PARA PIX DIRETO */}
                            <button
                                type="button"
                                onClick={() => setShowAccessModal(true)}
                                disabled={!pixDiretoPronto}
                                style={{
                                    width: '100%',
                                    maxWidth: '280px',
                                    background: pixDiretoPronto ? '#22c55e' : '#cbd5e1',
                                    color: '#fff',
                                    border: 'none',
                                    padding: '14px',
                                    borderRadius: '15px',
                                    fontWeight: '800',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    gap: '10px',
                                    marginBottom: '12px',
                                    cursor: pixDiretoPronto ? 'pointer' : 'not-allowed',
                                    transition: 'background 0.3s'
                                }}
                            >
                                <CheckCircle size={18}/> 
                                {pixDiretoPronto ? 'JÁ ENVIEI O COMPROVANTE' : `AGUARDE (${timerPixDireto}s)`}
                            </button>
                
                            <button
                                type="button"
                                onClick={onBack}
                                style={{
                                    background: 'none',
                                    border: 'none',
                                    color: '#64748b',
                                    fontSize: '13px',
                                    fontWeight: 'bold',
                                    cursor: 'pointer'
                                }}
                            >
                                Cancelar e voltar
                            </button>
                        </div>
                    </div>
                
                    {/* STATUS */}
                    <div style={{
                        background: '#f8fafc',
                        padding: '15px',
                        textAlign: 'center',
                        borderTop: '1px solid #e2e8f0'
                    }}>
                        {orderStatus === 'pendente' ? (
                            <div style={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                gap: '10px',
                                color: '#ef4444',
                                fontWeight: '900'
                            }}>
                                <Loader2 size={20} className="animate-spin" />
                                AGUARDANDO PAGAMENTO...
                            </div>
                        ) : (
                            <span style={{ color: '#22c55e', fontWeight: '900', fontSize: '18px' }}>
                                ✅ PAGO COM SUCESSO!
                            </span>
                        )}
                    </div>
                </div>
            )}

            {showAccessModal && (
                <div style={{ position: 'fixed', inset: 0, background: 'rgba(15, 23, 42, 0.95)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999 }}>
                    <div style={{ background: '#fff', padding: '40px', borderRadius: '40px', textAlign: 'center', maxWidth: '440px', width: '90%', boxShadow: '0 20px 25px -5px rgba(0,0,0,0.2)' }}>
                        
                        <div style={{ background: '#dcfce7', width: '80px', height: '80px', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 20px' }}>
                            <ShieldCheck size={40} color="#22c55e" />
                        </div>
            
                        <h2 style={{ fontSize: '26px', fontWeight: '900', color: '#0f172a', marginBottom: '10px' }}>Pedido Confirmado!</h2>
                        
                        <p style={{ color: '#64748b', fontSize: '14px', lineHeight: '1.5', marginBottom: '15px' }}>
                            Para agilizar, clique abaixo para encaminhar o seu <b>comprovante de pagamento</b> no WhatsApp da loja. Em seguida, o código de validade e o acesso à entrega serão liberados:
                        </p>
            
                        {/* ✅ EXIBIÇÃO DO TOKEN EM DESTAQUE */}
                        <div style={{ 
                            background: '#f8fafc', 
                            padding: '20px', 
                            borderRadius: '24px', 
                            border: '2px dashed #e2e8f0',
                            fontSize: '36px',
                            fontWeight: '900',
                            color: '#ef4444',
                            letterSpacing: '5px',
                            margin: '20px 0'
                        }}>
                            {paymentData?.codigoAcesso || '------'}
                        </div>
            
                        <p style={{ fontSize: '12px', color: '#94a3b8', marginBottom: '25px' }}>
                            Uma cópia deste código foi enviada para seu WhatsApp e E-mail.
                        </p>
            
                        <button 
                            type="button"
                            onClick={() => enviarWhatsApp(formData.pagamento, formData.endereco)} 
                            style={{ 
                                width: '100%', 
                                background: '#22c55e', 
                                color: '#fff', 
                                border: 'none', 
                                padding: '20px', 
                                borderRadius: '20px', 
                                fontWeight: '900', 
                                fontSize: '17px', 
                                display: 'flex', 
                                alignItems: 'center', 
                                justifyContent: 'center', 
                                gap: '12px',
                                transition: 'all 0.2s',
                                cursor: 'pointer' 
                            }}
                        >
                            <Send size={22}/> ENVIAR COMPROVANTE NO WHATSAPP
                        </button>
                        
                        <button 
                            type="button"
                            onClick={onFinalize}
                            style={{ 
                                background: 'none', 
                                border: 'none', 
                                marginTop: '20px', 
                                color: '#94a3b8', 
                                fontSize: '13px', 
                                fontWeight: '600',
                                cursor: 'pointer' 
                            }}
                        >
                            Fechar e voltar ao início
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
}